import { useTheme } from '@shopify/restyle';
import { IconAlertCircle, IconChevronRight, IconClock, IconRotateClockwise } from '@tabler/icons-react-native';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import {
  addDays,
  differenceInCalendarDays,
  getDaysInMonth,
  isSameDay,
  localeAwareFormat,
  startOfDay,
  startOfMonth,
} from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import type { Theme } from 'app/shopify';
import type { Loan } from 'app/types/loan';
import { getLoanETA, LoanETA, LoanStatus, LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { getLoanCalendarEnd, loanOccursOnDate } from './loan-calendar-utils';

const DETAILS_WIDTH = 336;

type PlannerGroup = 'attention' | 'ongoing' | 'upcoming' | 'completed';

interface LoanPlannerProps {
  currentMonth: Date;
  highlightedLoanId: Loan['id'] | null;
  loanType: LoanType;
  loans: Loan[];
  onDateHoverIn: (date: Date) => void;
  onDateHoverOut: () => void;
  onDateSelect: (date: Date) => void;
  onLoanHoverIn: (id: Loan['id']) => void;
  onLoanHoverOut: () => void;
  onLoanSelect: (id: Loan['id']) => void;
  selectedDate: Date | null;
  selectedLoanId: Loan['id'] | null;
}

function getPlannerGroup(loan: Loan): PlannerGroup {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late || eta === LoanETA.returned) {
    return 'attention';
  }

  if (eta === LoanETA.ongoing) {
    return 'ongoing';
  }

  if (eta === LoanETA.scheduled || eta === LoanETA.pending) {
    return 'upcoming';
  }

  return 'completed';
}

function getGroupAccent(group: PlannerGroup, theme: Theme) {
  if (group === 'attention') {
    return theme.colors['surface.error.default'];
  }

  if (group === 'ongoing') {
    return theme.colors['surface.brand.strong'];
  }

  if (group === 'upcoming') {
    return theme.colors['surface.info.default'];
  }

  return theme.colors['text.secondary'];
}

function getLoanAccent(loan: Loan, theme: Theme) {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late) {
    return theme.colors['surface.error.default'];
  }

  if (eta === LoanETA.returned) {
    return theme.colors['surface.warning.default'];
  }

  if (eta === LoanETA.scheduled || eta === LoanETA.pending) {
    return theme.colors['surface.info.default'];
  }

  if (eta === LoanETA.past) {
    return theme.colors['text.secondary'];
  }

  return theme.colors['surface.brand.strong'];
}

function getLoanSubtle(loan: Loan, theme: Theme) {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late) {
    return theme.colors['surface.brand.secondary-4.subtle'];
  }

  if (eta === LoanETA.returned) {
    return theme.colors['surface.warning.subtle'];
  }

  if (eta === LoanETA.scheduled || eta === LoanETA.pending) {
    return theme.colors['surface.info.subtle'];
  }

  if (eta === LoanETA.past) {
    return theme.colors['surface.primary-neutral'];
  }

  return theme.colors['surface.brand.subtle'];
}

function formatLoanRange(loan: Loan, dateFormat: string) {
  const start = new Date(loan.start);
  const end = new Date(loan.returned ?? loan.end);

  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    if (isSameDay(start, end)) {
      return localeAwareFormat(start, dateFormat);
    }

    return `${localeAwareFormat(start, 'd')}–${localeAwareFormat(end, dateFormat)}`;
  }

  return `${localeAwareFormat(start, dateFormat)} – ${localeAwareFormat(end, dateFormat)}`;
}

function getTimelineSegment(start: Date, end: Date, month: Date, daysInMonth: number) {
  const monthStart = startOfMonth(month);
  const monthEnd = addDays(monthStart, daysInMonth - 1);
  const visibleStart = start < monthStart ? monthStart : start;
  const visibleEnd = end > monthEnd ? monthEnd : end;

  if (visibleEnd < monthStart || visibleStart > monthEnd || visibleEnd < visibleStart) {
    return null;
  }

  const left = (differenceInCalendarDays(visibleStart, monthStart) / daysInMonth) * 100;
  const width = ((differenceInCalendarDays(visibleEnd, visibleStart) + 1) / daysInMonth) * 100;

  return {
    continuesAfter: end > monthEnd,
    continuesBefore: start < monthStart,
    left: `${String(left)}%` as `${number}%`,
    width: `${String(width)}%` as `${number}%`,
  };
}

function getLoanTimelineSegments(loan: Loan, today: Date, currentMonth: Date, daysInMonth: number) {
  const loanStart = startOfDay(new Date(loan.start));
  const plannedEnd = startOfDay(new Date(loan.end));
  const calendarEnd = getLoanCalendarEnd(loan, today);

  return {
    actualSegment:
      loan.status === LoanStatus.Returned && loan.returned
        ? getTimelineSegment(loanStart, startOfDay(new Date(loan.returned)), currentMonth, daysInMonth)
        : null,
    overdueSegment:
      loan.status === LoanStatus.Accepted && plannedEnd < today
        ? getTimelineSegment(addDays(plannedEnd, 1), calendarEnd, currentMonth, daysInMonth)
        : null,
    plannedSegment: getTimelineSegment(loanStart, plannedEnd, currentMonth, daysInMonth),
  };
}

type TimelineSegment = NonNullable<ReturnType<typeof getTimelineSegment>>;

function PlannedLoanSegment({
  active,
  accent,
  loan,
  segment,
  theme,
}: {
  active: boolean;
  accent: string;
  loan: Loan;
  segment: TimelineSegment;
  theme: Theme;
}) {
  return (
    <View
      style={{
        backgroundColor: getLoanSubtle(loan, theme),
        borderColor: accent,
        borderLeftWidth: segment.continuesBefore ? 0 : 1,
        borderRadius: 7,
        borderRightWidth: segment.continuesAfter ? 0 : 1,
        borderWidth: 1,
        height: active ? 18 : 14,
        left: segment.left,
        opacity: active ? 1 : 0.72,
        position: 'absolute',
        width: segment.width,
      }}
    />
  );
}

function ActualLoanSegment({ active, segment, theme }: { active: boolean; segment: TimelineSegment; theme: Theme }) {
  return (
    <View
      style={{
        backgroundColor: theme.colors['surface.warning.subtle'],
        borderColor: theme.colors['surface.warning.default'],
        borderRadius: 7,
        height: active ? 18 : 14,
        left: segment.left,
        position: 'absolute',
        width: segment.width,
      }}
    />
  );
}

function OverdueLoanSegment({ active, segment, theme }: { active: boolean; segment: TimelineSegment; theme: Theme }) {
  return (
    <View
      style={{
        backgroundColor: theme.colors['surface.error.subtle'],
        borderBottomRightRadius: segment.continuesAfter ? 0 : 7,
        borderColor: theme.colors['surface.error.default'],
        borderLeftWidth: 0,
        borderRightWidth: segment.continuesAfter ? 0 : 1,
        borderStyle: 'dashed',
        borderTopRightRadius: segment.continuesAfter ? 0 : 7,
        borderWidth: 1,
        height: active ? 18 : 14,
        left: segment.left,
        position: 'absolute',
        width: segment.width,
      }}
    />
  );
}

function LoanTimeline({
  active,
  currentMonth,
  loan,
  selectedDate,
}: {
  active: boolean;
  currentMonth: Date;
  loan: Loan;
  selectedDate: Date | null;
}) {
  const theme = useTheme<Theme>();
  const [today] = useReducer(
    (current: Date) => current,
    null,
    () => startOfDay(new Date()),
  );
  const daysInMonth = getDaysInMonth(currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, index) => addDays(startOfMonth(currentMonth), index));
  const { actualSegment, overdueSegment, plannedSegment } = getLoanTimelineSegments(
    loan,
    today,
    currentMonth,
    daysInMonth,
  );
  const accent = getLoanAccent(loan, theme);

  return (
    <View style={{ flex: 1, height: 76, justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
      <Row style={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }}>
        {days.map((date) => {
          const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
          const isToday = isSameDay(date, today);

          return (
            <View
              key={date.toISOString()}
              style={{
                backgroundColor: isSelected
                  ? theme.colors['surface.brand.subtle']
                  : date.getDay() === 0 || date.getDay() === 6
                    ? theme.colors['surface.primary-neutral']
                    : 'transparent',
                borderLeftColor: isToday
                  ? theme.colors['surface.brand.default']
                  : theme.colors['surface.tertiary-neutral'],
                borderLeftWidth: isToday ? 1.5 : 0.5,
                flex: 1,
              }}
            />
          );
        })}
      </Row>

      {plannedSegment ? (
        <PlannedLoanSegment active={active} accent={accent} loan={loan} segment={plannedSegment} theme={theme} />
      ) : null}

      {actualSegment ? <ActualLoanSegment active={active} segment={actualSegment} theme={theme} /> : null}

      {overdueSegment ? <OverdueLoanSegment active={active} segment={overdueSegment} theme={theme} /> : null}
    </View>
  );
}

function LoanPlannerRow({
  currentMonth,
  highlightedLoanId,
  loan,
  loanType,
  onLoanHoverIn,
  onLoanHoverOut,
  onLoanSelect,
  selectedDate,
  selectedLoanId,
}: Pick<
  LoanPlannerProps,
  | 'currentMonth'
  | 'highlightedLoanId'
  | 'loanType'
  | 'onLoanHoverIn'
  | 'onLoanHoverOut'
  | 'onLoanSelect'
  | 'selectedDate'
  | 'selectedLoanId'
> & { loan: Loan }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const navigation = useNavigation();
  const eta = getLoanETA(loan);
  const dateMatches = selectedDate ? loanOccursOnDate(loan, selectedDate) : false;
  const active = highlightedLoanId === loan.id || selectedLoanId === loan.id || dateMatches;
  const otherUser = loanType === LoanType.Borrowing ? loan.lender : loan.borrower;
  const otherUserName = otherUser.profile
    ? `${otherUser.profile.firstName} ${otherUser.profile.lastName}`
    : t('deletedUser', { ns: 'common' });
  const accent = getLoanAccent(loan, theme);
  const dateFormat = t('dateFormat_MMMMd', { ns: 'common' });
  const statusDate =
    eta === LoanETA.scheduled || eta === LoanETA.pending ? new Date(loan.start) : new Date(loan.returned ?? loan.end);
  const statusPrefix =
    eta === LoanETA.scheduled || eta === LoanETA.pending
      ? t('calendar.planner.starts')
      : eta === LoanETA.returned || eta === LoanETA.past
        ? t('calendar.planner.returned')
        : t('calendar.planner.due');

  return (
    <Pressable
      accessibilityLabel={`${loan.listing.item.name}, ${otherUserName}, ${formatLoanRange(loan, dateFormat)}`}
      accessibilityRole="button"
      accessibilityState={{ selected: selectedLoanId === loan.id }}
      onBlur={onLoanHoverOut}
      onFocus={() => onLoanHoverIn(loan.id)}
      onHoverIn={() => onLoanHoverIn(loan.id)}
      onHoverOut={onLoanHoverOut}
      onPress={() => {
        onLoanSelect(loan.id);
        navigateToLoan(navigation, loan.id, loan.listing.id);
      }}
      style={({ pressed }) => ({
        backgroundColor: active ? theme.colors['surface.selected'] : theme.colors['surface.default'],
        opacity: pressed ? 0.72 : 1,
      })}
    >
      <Row
        style={{
          borderTopColor: theme.colors['surface.tertiary-neutral'],
          borderTopWidth: 1,
          minHeight: 76,
        }}
      >
        <Row
          alignItems="center"
          gap="spacing.general.x3"
          paddingHorizontal="spacing.general.x4"
          style={{
            borderLeftColor: active ? accent : 'transparent',
            borderLeftWidth: 3,
            width: DETAILS_WIDTH,
          }}
        >
          <ListingThumbnail hideCorpoBadge size={42} {...loan.listing} isFound={Boolean(loan.listing.lender)} />

          <Column flex={1} minWidth={0}>
            <Text fontSize={14} fontWeight="600" numberOfLines={1}>
              {loan.listing.item.name}
            </Text>
            <Row alignItems="center" gap="spacing.general.x1">
              {otherUser.profile?.picture ? (
                <PCImage
                  aspectRatio={1}
                  height={16}
                  source={{ uri: getImageVariantUrl(otherUser.profile.picture, 'mini') }}
                  style={{ borderRadius: 8 }}
                />
              ) : null}
              <Text color="text.secondary" flex={1} fontSize={12} numberOfLines={1}>
                {otherUserName} · {formatLoanRange(loan, dateFormat)}
              </Text>
            </Row>
            <Row alignItems="center" gap="spacing.general.x1" marginTop="spacing.general.x1">
              <View style={{ backgroundColor: accent, borderRadius: 3, height: 6, width: 6 }} />
              <Text color="text.secondary" fontSize={11} numberOfLines={1}>
                {t(`card.status.${eta}`)} · {statusPrefix} {localeAwareFormat(statusDate, dateFormat)}
              </Text>
            </Row>
          </Column>

          <IconChevronRight color={theme.colors['text.secondary']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
        </Row>

        <LoanTimeline active={active} currentMonth={currentMonth} loan={loan} selectedDate={selectedDate} />
      </Row>
    </Pressable>
  );
}

function PlannerGroupHeading({ count, group }: { count: number; group: PlannerGroup }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const accent = getGroupAccent(group, theme);
  const Icon = group === 'attention' ? IconAlertCircle : group === 'ongoing' ? IconRotateClockwise : IconClock;

  return (
    <Row
      alignItems="center"
      backgroundColor="surface.primary-neutral"
      gap="spacing.general.x2"
      paddingHorizontal="spacing.general.x4"
      style={{ height: 36 }}
    >
      <Icon color={accent} size={15} strokeWidth={ICON_STROKE_WIDTH} />
      <Text fontSize={12} fontWeight="600">
        {t(`calendar.planner.groups.${group}`)}
      </Text>
      <Text color="text.secondary" fontSize={11} opacity={0.72}>
        {count}
      </Text>
    </Row>
  );
}

export function LoanPlanner({
  currentMonth,
  highlightedLoanId,
  loanType,
  loans,
  onDateHoverIn,
  onDateHoverOut,
  onDateSelect,
  onLoanHoverIn,
  onLoanHoverOut,
  onLoanSelect,
  selectedDate,
  selectedLoanId,
}: LoanPlannerProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const [today] = useReducer(
    (current: Date) => current,
    null,
    () => startOfDay(new Date()),
  );
  const daysInMonth = getDaysInMonth(currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, index) => addDays(startOfMonth(currentMonth), index));
  const groups = (() => {
    const byGroup = new Map<PlannerGroup, Loan[]>(
      (['attention', 'ongoing', 'upcoming', 'completed'] as const).map((group) => [group, []]),
    );

    loans.forEach((loan) => byGroup.get(getPlannerGroup(loan))?.push(loan));
    byGroup.forEach((groupLoans) => groupLoans.sort((a, b) => new Date(a.end).getTime() - new Date(b.end).getTime()));

    return [...byGroup.entries()].filter((entry): entry is [PlannerGroup, Loan[]] => entry[1].length > 0);
  })();

  return (
    <Column
      backgroundColor="surface.default"
      borderColor="surface.disabled"
      borderRadius="radius.lg"
      borderWidth={1}
      style={{
        overflow: 'hidden',
        boxShadow: `0px 8px 24px ${theme.colors['shadow.strong']}`,
      }}
      testID="loan-planner"
    >
      <Row style={{ minHeight: 64 }}>
        <Column
          justifyContent="center"
          paddingHorizontal="spacing.general.x4"
          style={{
            borderRightColor: theme.colors['surface.tertiary-neutral'],
            borderRightWidth: 1,
            width: DETAILS_WIDTH,
          }}
        >
          <Text fontSize={12} fontWeight="600">
            {t('calendar.planner.loanColumn')}
          </Text>
          <Text color="text.secondary" fontSize={11} opacity={0.72}>
            {t('calendar.activitySummary', {
              count: loans.length,
              month: localeAwareFormat(currentMonth, 'MMMM'),
            })}
          </Text>
        </Column>

        <Row flex={1}>
          {days.map((date) => {
            const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
            const isToday = isSameDay(date, today);
            const dayLoanCount = loans.filter((loan) => loanOccursOnDate(loan, date, today)).length;

            return (
              <Pressable
                accessibilityLabel={t('calendar.dateAccessibility', {
                  count: dayLoanCount,
                  date: localeAwareFormat(date, 'PPPP'),
                })}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                key={date.toISOString()}
                onHoverIn={() => onDateHoverIn(date)}
                onHoverOut={onDateHoverOut}
                onPress={() => onDateSelect(date)}
                style={({ pressed }) => ({
                  alignItems: 'center',
                  backgroundColor:
                    isSelected || pressed
                      ? theme.colors['surface.brand.subtle']
                      : date.getDay() === 0 || date.getDay() === 6
                        ? theme.colors['surface.primary-neutral']
                        : 'transparent',
                  borderLeftColor: isToday
                    ? theme.colors['surface.brand.default']
                    : theme.colors['surface.tertiary-neutral'],
                  borderLeftWidth: isToday ? 1.5 : 0.5,
                  flex: 1,
                  justifyContent: 'center',
                })}
              >
                <Text color="text.secondary" fontSize={8} fontWeight="600" opacity={0.72}>
                  {localeAwareFormat(date, 'EEEEE')}
                </Text>
                <Text
                  color={isToday ? 'text.brand' : 'text.secondary'}
                  fontSize={10}
                  fontWeight={isToday ? '700' : '500'}
                >
                  {date.getDate()}
                </Text>
              </Pressable>
            );
          })}
        </Row>
      </Row>

      {groups.map(([group, groupLoans]) => (
        <Column key={group}>
          <PlannerGroupHeading count={groupLoans.length} group={group} />
          {groupLoans.map((loan) => (
            <LoanPlannerRow
              currentMonth={currentMonth}
              highlightedLoanId={highlightedLoanId}
              key={loan.id}
              loan={loan}
              loanType={loanType}
              onLoanHoverIn={onLoanHoverIn}
              onLoanHoverOut={onLoanHoverOut}
              onLoanSelect={onLoanSelect}
              selectedDate={selectedDate}
              selectedLoanId={selectedLoanId}
            />
          ))}
        </Column>
      ))}
    </Column>
  );
}
